import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { withRouter, Link, useHistory } from 'react-router-dom';
import {
  Layout,
  Modal,
  Button,
  Avatar,
  Row,
  Col,
  Popover,
  Divider,
  Space,
  Typography,
  Input,
  Card,
  List,
  Badge,
  Grid,
} from 'antd';
import {
  MenuFoldOutlined,
  ExclamationCircleOutlined,
  SearchOutlined,
  BellOutlined,
  LoadingOutlined,
  ReloadOutlined,
  MessageOutlined,
  UserOutlined,
  CrownOutlined,
} from '@ant-design/icons';
import { isMobile } from 'react-device-detect';
// actions
import { logout } from '../redux/actions/AuthActions';
import AppDrawer from './AppDrawer';
import IntlMessages from '../components/IntlMessage';
import { injectIntl } from 'react-intl';
import useDebouncedCallback from '../hooks/useDebouncedCallback';
import defaultAvatar from '../assets/img/avatar.jpg';

import orderApi from '../api/order';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import cacheApi from '../api/cache';
import { rateUsd } from '../utils/constant';
import Currency from '../components/Currency';
import Pusher from 'pusher-js';
import { useConversationList } from '../routes/direct-messages/context/PubnubConversationListContext';
import { getUnreadMessageCount } from '../api/direct-messages';
import GoogleTranslateButton from '../components/Language/GoogleTranslateButton';

dayjs.extend(relativeTime);

const { Text } = Typography;
const { Header } = Layout;
const { confirm } = Modal;

function AppHeader() {
  const history = useHistory();
  // const { globalUnread } = useConversationList();
  const [visible, setVisible] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const screens = Grid.useBreakpoint();
  const authUser = useSelector((state) => state.auth.authUser);
  const config = useSelector((state) => state.config);
  const dispatch = useDispatch();
  const isMount = useRef(false);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingCache, setLoadingCache] = useState(false);
  const [loadingCacheType, setLoadingCacheType] = useState(null);
  const [cachePopoverVisible, setCachePopoverVisible] = useState(false);
  const [total, setTotal] = useState(1);
  const [filter, setFilter] = useState({
    page: 1,
    limit: 10,
    keyword: '',
    order_by: 'id',
    order_dir: 'DESC',
  });
  const [notifications, setNotifications] = useState([]);
  // State để điều khiển xem tất cả/thêm thông báo
  const [showAllNotifications, setShowAllNotifications] = useState(false);
  // Lưu trạng thái active/read cho notification
  // Load notifications from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem('notifications');
    if (stored) {
      try {
        setNotifications(JSON.parse(stored));
      } catch (e) {
        setNotifications([]);
      }
    }
  }, []);
  const [unreadCount, setUnreadCount] = useState(null);
  useEffect(() => {
    getUnreadMessageCount().then((res) => {
      if (res) {
        setUnreadCount(res.total_unread_count || null);
      }
    });
  }, []);

  // Save notifications to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('notifications', JSON.stringify(notifications));
  }, [notifications]);
  const [isOneSignalInitialized, setIsOneSignalInitialized] = useState(false);

  // side effects
  useEffect(() => {
    isMount.current = true;
    if (isMount) {
      setLoading(true);
      if (filter.keyword.length > 1) {
        Promise.all([orderApi.getAll(filter)])
          .then((res) => {
            const orderData = res[0]?.data;
            if (Array.isArray(orderData)) {
              setOrders(orderData);
            }
            if (total !== res[0].total) {
              setTotal(res[0].total);
            }
            setLoading(false);
          })
          .catch((error) => {
            console.error(error);
            setLoading(false);
          });
      } else {
        setOrders([]);
        setLoading(false);
      }
    }

    return () => {
      isMount.current = false;
    };
  }, [filter]);

  useEffect(() => {
    if (filter.keyword?.length > 1) {
      if (!isSearching) {
        setIsSearching(true);
      }
    } else {
      setIsSearching(false);
    }
  }, [filter]);

  useEffect(() => {
    // === Pusher Integration ===
    const pusher = new Pusher('44a454eb201ddb09cad7', {
      // Đây là App Key của bạn
      cluster: 'ap1', // Đây là Cluster của bạn
      encrypted: true, // Đặt true nếu bạn sử dụng SSL
    });

    // Đăng ký kênh (ví dụ: 'my-channel')
    const channel = pusher.subscribe('my-channel'); // Thay thế bằng tên kênh của bạn

    // Lắng nghe sự kiện (ví dụ: 'new-activity')
    channel.bind('new-activity', function (data) {
      // Thêm notification mới với trạng thái read: false, active: false
      setNotifications((prevNotifications) => [
        {
          title: data.title,
          body: data.message,
          timestamp: new Date(),
          read: false,
          active: false,
        },
        ...prevNotifications,
      ]);
    });

    // === End Pusher Integration ===

    return () => {
      // Clean up Pusher connection
      if (channel) {
        channel.unbind_all();
        pusher.unsubscribe('my-channel');
      }
      pusher.disconnect();
    };
  }, []);

  const toggleDrawer = (visible) => {
    setVisible(visible);
  };

  const getAvatarUrl = (user) => {
    if (!user) return null;
    return (
      user.profile_picture ||
      user.image ||
      user.avatar ||
      user.user?.profile_picture ||
      user.user?.image ||
      user.user?.avatar ||
      null
    );
  };

  const getDisplayName = (user) => {
    if (!user) return '';
    const fullname = user.fullname || user.full_name;
    if (fullname) return fullname;
    const first = user.first_name || user.firstname || user.user?.first_name || user.user?.firstname || '';
    const last = user.last_name || user.lastname || user.user?.last_name || user.user?.lastname || '';
    const combined = `${first} ${last}`.trim();
    if (combined) return combined;
    return user.username || user.email || 'User';
  };

  // Notification messages from the backend mark bold spans with **like this**
  // (e.g. "**Anny** liked your voice.") — render those spans as <strong>, not literal asterisks.
  const renderBoldText = (text) => {
    if (!text) return null;
    return text.split(/\*\*(.+?)\*\*/g).map((part, i) =>
      i % 2 === 1 ? <strong key={i}>{part}</strong> : part
    );
  };

  const displayEmail =
    authUser?.email ||
    authUser?.user?.email ||
    authUser?.username ||
    authUser?.user?.username ||
    '';

  const avatarUrl = getAvatarUrl(authUser);
  const avatarSrc = avatarUrl || defaultAvatar;
  const displayName = getDisplayName(authUser);
  const avatarFallback = displayName ? displayName.charAt(0).toUpperCase() : <UserOutlined />;

  const onLogout = () => {
    confirm({
      title: 'Are you sure to log out?',
      icon: <ExclamationCircleOutlined />,
      okText: 'OK',
      cancelText: 'Cancel',
      onOk: () => {
        dispatch(logout()).then(() => {
          window.location.href = '/login';
        });
      },
    });
  };

  const prefix = (
    <SearchOutlined
      style={{
        fontSize: 16,
        color: '#1890ff',
      }}
    />
  );

  const handleChangeSearch = useDebouncedCallback(async (e) => {
    const value = e?.target?.value ?? '';
    setFilter((filter) => {
      return { ...filter, keyword: value };
    });
  }, 300);

  const onChangePagination = (page, pageSize) => {
    setFilter({
      ...filter,
      page,
      limit: pageSize,
    });
  };

  const onClose = () => {
    setIsSearching(false);
  };

  const onFocus = useDebouncedCallback(() => {
    if (filter.keyword?.length > 1) {
      if (!isSearching) {
        setIsSearching(true);
      }
    } else {
      setIsSearching(false);
    }
  }, 500);

  const handleResetCache = () => {
    setLoadingCache(true);
    cacheApi
      .reset()
      .then(() => {
        setLoadingCache(false);
      })
      .catch((error) => {
        console.error(error);
        setLoadingCache(false);
      });
  };

  const handleResetCacheByType = (types) => {
    const key = Array.isArray(types) ? types[0] : types;
    setLoadingCacheType(key);
    cacheApi
      .resetByType(types)
      .then(() => {
        setLoadingCacheType(null);
      })
      .catch((error) => {
        console.error(error);
        setLoadingCacheType(null);
      });
  };

  const cacheMenuContent = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '4px 0', minWidth: 160 }}>
      {[
        { label: 'Service', type: 'service' },
        { label: 'Product', type: 'product' },
        { label: 'Post', type: 'post' },
        { label: 'Widget', type: 'widget' },
        { label: 'Category', type: 'category' },
      ].map(({ label, type }) => (
        <Button
          key={type}
          size="small"
          loading={loadingCacheType === type}
          disabled={!!loadingCacheType}
          onClick={() => handleResetCacheByType([type])}
          style={{
            textAlign: 'left',
            borderRadius: 6,
            fontWeight: 500,
          }}
        >
          {label}
        </Button>
      ))}
      <Button
        size="small"
        danger
        loading={loadingCacheType === 'all'}
        disabled={!!loadingCacheType}
        onClick={() => handleResetCacheByType(['all'])}
        style={{ borderRadius: 6, fontWeight: 700 }}
      >
        Reset All
      </Button>
    </div>
  );

  const handleMarkAllAsRead = async () => {
    // Đánh dấu tất cả là đã đọc
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  };

  // Đánh dấu 1 notification là active và đã đọc
  const handleSetActive = (idx) => {
    setNotifications((prev) =>
      prev.map((n, i) => ({
        ...n,
        active: i === idx,
        read: i === idx ? true : n.read,
      }))
    );
  };

  const notificationContent = (
    <div style={{ width: 300 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 10 }}>
        <Col>
          <Text strong>
            <IntlMessages id="global.notifications" />
          </Text>
        </Col>
        <Col>
          <Button type="link" onClick={handleMarkAllAsRead}>
            <IntlMessages id="global.mark_all_as_read" />
          </Button>
        </Col>
      </Row>
      <Divider style={{ margin: '10px 0' }} />
      <div style={{ maxHeight: 400, overflowY: 'auto', padding: 4 }}>
        <List
          itemLayout="horizontal"
          dataSource={
            showAllNotifications ? notifications : notifications.slice(0, 20)
          }
          renderItem={(item, idx) => {
            // Style cho từng trạng thái (nhẹ nhàng, hiện đại)
            let background = '#fff';
            let borderLeft = '3px solid transparent';
            let iconColor = '#bfbfbf';
            if (item.active) {
              background = '#f0f7ff';
              borderLeft = '3px solid #1890ff';
              iconColor = '#1890ff';
            } else if (!item.read) {
              background = '#fafcff';
              borderLeft = '3px solid #52c41a';
              iconColor = '#52c41a';
            } else {
              background = '#f7f7f7';
              borderLeft = '3px solid #e0e0e0';
              iconColor = '#bfbfbf';
            }
            return (
              <List.Item
                style={{
                  background,
                  borderLeft,
                  borderRadius: 6,
                  marginBottom: 8,
                  boxShadow: item.active ? '0 2px 8px #e6f7ff' : 'none',
                  fontWeight: !item.read ? 500 : 400,
                  cursor: 'pointer',
                  transition: 'background 0.2s',
                  padding: 12,
                  minHeight: 56,
                  display: 'flex',
                  alignItems: 'flex-start',
                }}
                onClick={() => handleSetActive(idx)}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = '#f5faff')
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = background)
                }
              >
                {/* Icon trạng thái nhỏ, sát tiêu đề */}
                <div
                  style={{
                    width: 12,
                    height: 12,
                    marginRight: 10,
                    marginTop: 4,
                  }}
                >
                  <span
                    style={{
                      display: 'inline-block',
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: iconColor,
                    }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: 2,
                    }}
                  >
                    <span
                      style={{
                        fontWeight: 600,
                        fontSize: 15,
                        color: '#222',
                        marginRight: 8,
                      }}
                    >
                      {item.title ? renderBoldText(item.title) : 'New Activity'}
                    </span>
                    <span
                      style={{
                        fontSize: 12,
                        color: '#999',
                        marginLeft: 'auto',
                      }}
                    >
                      {dayjs(item.timestamp).fromNow()}
                    </span>
                  </div>
                  <div
                    style={{
                      color: '#444',
                      fontSize: 13,
                      whiteSpace: 'pre-line',
                    }}
                  >
                    {item.body ? renderBoldText(item.body) : '-'}
                  </div>
                </div>
              </List.Item>
            );
          }}
          locale={{
            emptyText: <IntlMessages id="no_new_notifications" />,
          }}
        />
        {!showAllNotifications && notifications.length > 20 && (
          <div style={{ textAlign: 'center', marginTop: 8 }}>
            <Button
              size="small"
              type="link"
              onClick={() => setShowAllNotifications(true)}
            >
              <IntlMessages id="global.see-more" />
            </Button>
          </div>
        )}
        {showAllNotifications && notifications.length > 20 && (
          <div style={{ textAlign: 'center', marginTop: 8 }}>
            <Button
              size="small"
              type="link"
              onClick={() => setShowAllNotifications(false)}
            >
              <IntlMessages id="global.hide-less" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <Header className="site-header-background" style={{ padding: '0 16px', position: 'relative', zIndex: 1000 }}>
      <Row wrap={false} justify="space-between" align="middle" style={{ width: '100%', height: '100%' }}>
        <Col>
          {!screens.lg && (
            <Space size="small">
              <Button
                type="link"
                icon={<MenuFoldOutlined style={{ fontSize: 22, color: '#333' }} />}
                onClick={() => toggleDrawer(true)}
              />
              <Button
                type="default"
                onClick={() => history.push('/app/lionrose')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  color: '#8c8c8c',
                  fontWeight: '500',
                  padding: '0 12px',
                  border: '1px solid #d9d9d9',
                  borderRadius: '4px',
                  height: '32px',
                  fontSize: '13px',
                  background: 'rgba(255, 255, 255, 0.8)'
                }}
                icon={<CrownOutlined style={{ fontSize: 16, color: '#da1f27', opacity: 0.8 }} />}
              >
                <span className="header-brand-label">LionRose</span>
              </Button>
            </Space>
          )}
        </Col>

        <Col flex="auto" style={{ minWidth: 0 }}>
          <Row wrap={false} align="middle" justify="end" gutter={[screens.lg ? 16 : 8, 0]}>

            <Col>
              <Badge count={unreadCount} offset={[5, 0]}>
                <Button
                  style={{
                    backgroundColor: '#da1f27',
                    color: '#fff',
                    border: 'none',
                  }}
                  icon={<MessageOutlined style={{ fontSize: 20, color: '#fff' }} />}
                  onClick={() => history.push('/app/direct-messages')}
                />
              </Badge>
            </Col>

            {screens.lg && (
              <Col>
                <Popover
                  content={cacheMenuContent}
                  trigger="click"
                  placement="bottomRight"
                  open={cachePopoverVisible}
                  onOpenChange={setCachePopoverVisible}
                  title={<span style={{ fontWeight: 600, fontSize: 13 }}>🔄 Reset Cache</span>}
                >
                  <Button
                    type="primary"
                    shape="round"
                    loading={loadingCache}
                    style={{ backgroundColor: '#da1f27', boxShadow: 'none' }}
                  >
                    <IntlMessages id="global.cache_reset" />
                  </Button>
                </Popover>
              </Col>
            )}

            {screens.lg && (
              <Col style={{ position: 'relative' }}>
                <Input
                  placeholder="Search..."
                  size="large"
                  prefix={prefix}
                  onChange={handleChangeSearch}
                  style={{ zIndex: '1001' }}
                  allowClear
                  onFocus={onFocus}
                />
                {isSearching && orders.length > 0 && (
                  <>
                    <Card
                      title={<IntlMessages id="global.orders" />}
                      style={{
                        position: 'absolute',
                        top: 'calc(100% + 6px)',
                        right: '0',
                        zIndex: '1002',
                        boxShadow: '1px 2px 5px #eee',
                        minWidth: '600px',
                      }}
                    >
                      <List
                        pagination={{
                          size: 'small',
                          showSizeChanger: true,
                          pageSizeOptions: ['10', '20', '50'],
                          total: total,
                          current: filter.page,
                          pageSize: filter.limit,
                          onChange: onChangePagination,
                        }}
                        loading={loading}
                        dataSource={orders}
                        renderItem={(order) => (
                          <List.Item>
                            <List.Item.Meta
                              title={
                                <Link
                                  to={`/app/orders/details/${order.id}`}
                                  onClick={() => setIsSearching(false)}
                                >
                                  {order.order_no}
                                </Link>
                              }
                              description={<OrderItem order={order} />}
                            />
                          </List.Item>
                        )}
                      />
                    </Card>
                    <div
                      style={{
                        position: 'fixed',
                        top: '0',
                        bottom: '0',
                        right: '0',
                        left: '0',
                        zIndex: '1000',
                      }}
                      onClick={onClose}
                    />
                  </>
                )}
              </Col>
            )}

            <Col>
              <Popover
                content={notificationContent}
                trigger="click"
                placement="bottomRight"
                overlayStyle={{ padding: 0 }}
              >
                <Badge
                  count={notifications.filter((n) => !n.read).length}
                  offset={[5, 0]}
                >
                  <Button
                    style={{
                      backgroundColor: '#808080',
                      color: '#fff',
                      border: 'none',
                    }}
                    icon={<BellOutlined style={{ fontSize: 20, color: '#fff' }} />}
                  />
                </Badge>
              </Popover>
            </Col>

            <Col>
              <GoogleTranslateButton />
            </Col>

            <Col>
              <Popover
                trigger="click"
                className="header-actions"
                placement="bottomRight"
                content={
                  <>
                    <Row gutter={[20, 20]}>
                      <Col>
                        <Row align="middle" justify="space-around">
                          <Avatar
                            src={avatarSrc}
                            style={{ backgroundColor: '#f56a00' }}
                            icon={!avatarUrl ? <UserOutlined /> : null}
                          >
                            {!avatarUrl ? avatarFallback : null}
                          </Avatar>
                        </Row>
                      </Col>
                      <Col>
                        <Text strong>
                          {displayName || <IntlMessages id="global.user" />}
                        </Text>
                        <br />
                        <Text style={{ fontSize: '12px' }} type="secondary">
                          {displayEmail}
                        </Text>
                      </Col>
                    </Row>
                    <Divider style={{ marginTop: '10px', marginBottom: '10px' }} />
                    <Link to="/app/profile">
                      <Button type="link">
                        <IntlMessages id="global.account_information" />
                      </Button>
                    </Link>
                    <Divider style={{ marginTop: '10px', marginBottom: '10px' }} />
                    <Button type="link" onClick={() => onLogout()}>
                      <Space>
                        <IntlMessages id="global.signout" />
                      </Space>
                    </Button>
                  </>
                }
              >
                <Avatar
                  src={avatarSrc}
                  style={{ backgroundColor: '#f56a00', cursor: 'pointer' }}
                  icon={!avatarUrl ? <UserOutlined /> : null}
                >
                  {!avatarUrl ? avatarFallback : null}
                </Avatar>
              </Popover>
            </Col>
          </Row>
        </Col>
      </Row>

      <AppDrawer visible={visible} onClose={() => toggleDrawer(false)} />
    </Header>
  );
}

const OrderItem = (props) => {
  const { order } = props;
  return (
    <Row>
      <Col span={8}>
        <Text type={order.roundtrip && 'success'}>
          {order?.hop?.start_stop?.title} - {order?.hop?.end_stop?.title}
        </Text>
      </Col>
      <Col span={8}>
        <Space direction="vertical">
          <Text>{dayjs(order.date).format('ddd, MMM DD, YYYY')}</Text>
          {order?.return_date && (
            <Text>{dayjs(order.return_date).format('ddd, MMM DD, YYYY')}</Text>
          )}
        </Space>
      </Col>
      <Col span={8}>
        <Text>
          {order?.customer?.fullname}
          {order?.customer?.company_shortname &&
            ` (${order?.customer?.company_shortname})`}
        </Text>
      </Col>
    </Row>
  );
};

export default injectIntl(withRouter(AppHeader));
